import React, { useState } from 'react';
import { KeyRound, AlertTriangle, RotateCcw, Fingerprint } from 'lucide-react';

export const AuthSimulator: React.FC = () => {
  const [mode, setMode] = useState<'mfa' | 'passkey' | 'lockout'>('mfa');
  const [step, setStep] = useState(0);
  const [failedAttempts, setFailedAttempts] = useState(1);

  const handleModeChange = (newMode: 'mfa' | 'passkey' | 'lockout') => {
    setMode(newMode);
    setStep(0);
    setFailedAttempts(newMode === 'lockout' ? 1 : 0);
  };

  return (
    <div className="window-chrome-wrapper" id="interactive-console">
      <div className="window-chrome">
        <div className="chrome-header">
          <div className="chrome-dots">
            <div className="chrome-dot dot-red" />
            <div className="chrome-dot dot-yellow" />
            <div className="chrome-dot dot-green" />
          </div>
          <div className="chrome-title">draway-auth-runtime-debugger [env: ak_preview_live]</div>
          <div style={{ width: 40 }} />
        </div>

        <div className="chrome-body">
          <div className="auth-sim-tabs">
            <button
              type="button"
              className={`sim-tab-btn ${mode === 'mfa' ? 'active' : ''}`}
              onClick={() => handleModeChange('mfa')}
            >
              MFA STATE MACHINE
            </button>
            <button
              type="button"
              className={`sim-tab-btn ${mode === 'passkey' ? 'active' : ''}`}
              onClick={() => handleModeChange('passkey')}
            >
              WEBAUTHN PASSKEYS
            </button>
            <button
              type="button"
              className={`sim-tab-btn ${mode === 'lockout' ? 'active' : ''}`}
              onClick={() => handleModeChange('lockout')}
            >
              LOCKOUT DEFENSE
            </button>
          </div>

          <div className="auth-sim-screen">
            {mode === 'mfa' && (
              step === 0 ? (
                <>
                  <div className="sim-log-line"><span className="sim-prompt">&gt;</span> POST /v1/auth/login</div>
                  <div className="sim-log-line" style={{ color: '#9CA3AF' }}>  Payload: &#123; email: &quot;alex@enterprise.io&quot;, pass: &quot;••••••••&quot; &#125;</div>
                  <div className="sim-log-line" style={{ color: '#6D7CFF' }}>← HTTP/1.1 200 OK (Partial Auth Session)</div>
                  <div className="sim-log-line">
                    &nbsp;&nbsp;&#123; <span style={{ color: '#6D7CFF' }}>&quot;mfaRequired&quot;</span>: true, <span style={{ color: '#8C98FF' }}>&quot;mfaToken&quot;</span>: &quot;mfa_tok_9x28fa...&quot;, &quot;methods&quot;: [&quot;totp&quot;, &quot;email&quot;, &quot;whatsapp&quot;] &#125;
                  </div>
                  <div className="sim-log-line" style={{ color: '#6B7280' }}>/* Session is strictly isolated. Full access requires second factor proof. */</div>
                </>
              ) : (
                <>
                  <div className="sim-log-line"><span className="sim-prompt">&gt;</span> POST /v1/auth/verify-login</div>
                  <div className="sim-log-line" style={{ color: '#9CA3AF' }}>  Headers: &#123; &quot;x-mfa-token&quot;: &quot;mfa_tok_9x28fa...&quot; &#125;</div>
                  <div className="sim-log-line" style={{ color: '#9CA3AF' }}>  Payload: &#123; code: &quot;582049&quot;, method: &quot;totp&quot; &#125;</div>
                  <div className="sim-log-line" style={{ color: '#10B981' }}>← HTTP/1.1 200 OK (Verified &amp; Authenticated)</div>
                  <div className="sim-log-line"><span className="sim-success-badge">AES-256-GCM SEED DECRYPTED &amp; ARGON2ID VERIFIED</span></div>
                  <div className="sim-log-line" style={{ color: '#6D7CFF' }}>  Cookies Set: draway_at (HttpOnly, Secure, SameSite: Lax)</div>
                  <div className="sim-log-line" style={{ color: '#10B981' }}>  &#123; &quot;user&quot;: &#123; &quot;id&quot;: &quot;usr_9981&quot;, &quot;tenant&quot;: &quot;acme-corp&quot;, &quot;role&quot;: &quot;admin&quot; &#125; &#125;</div>
                </>
              )
            )}

            {mode === 'passkey' && (
              step === 0 ? (
                <>
                  <div className="sim-log-line"><span className="sim-prompt">&gt;</span> auth.loginWithPasskey()</div>
                  <div className="sim-log-line" style={{ color: '#6B7280' }}>/* Initiating FIDO2 WebAuthn v13 handshake */</div>
                  <div className="sim-log-line"><span className="sim-prompt">&gt;</span> POST /v1/mfa/passkey/login/options</div>
                  <div className="sim-log-line" style={{ color: '#6D7CFF' }}>← Challenge: &quot;ch_948f1029baec&quot; (rpId: auth.yourdomain.com)</div>
                  <div className="sim-log-line" style={{ color: '#8C98FF' }}>  Awaiting Biometric Assertion (TouchID / FaceID / YubiKey)...</div>
                </>
              ) : (
                <>
                  <div className="sim-log-line" style={{ color: '#10B981' }}>✓ Biometric Hardware Assertion Signed by Authenticator</div>
                  <div className="sim-log-line"><span className="sim-prompt">&gt;</span> POST /v1/mfa/passkey/login/verify</div>
                  <div className="sim-log-line" style={{ color: '#9CA3AF' }}>  Payload: &#123; credentialId: &quot;cred_88fa...&quot;, signature: &quot;MEQCID...&quot; &#125;</div>
                  <div className="sim-log-line" style={{ color: '#10B981' }}>← HTTP/1.1 200 OK (Instant Zero-Password Login)</div>
                  <div className="sim-log-line"><span className="sim-success-badge">FIDO2 VERIFIED • 0 PASSWORDS TRANSMITTED • 0 EXPOSURE</span></div>
                  <div className="sim-log-line" style={{ color: '#6D7CFF' }}>  Session active: 100% Phishing-Proof Architecture</div>
                </>
              )
            )}

            {mode === 'lockout' && (
              failedAttempts < 5 ? (
                <>
                  <div className="sim-log-line"><span className="sim-prompt">&gt;</span> POST /v1/auth/verify-login (Attempt {failedAttempts} of 5)</div>
                  <div className="sim-log-line" style={{ color: '#9CA3AF' }}>  Payload: &#123; code: &quot;000000&quot;, method: &quot;totp&quot; &#125;</div>
                  <div className="sim-log-line" style={{ color: '#FFBD2E' }}>← HTTP/1.1 401 Unauthorized: Invalid MFA verification code</div>
                  <div className="sim-log-line" style={{ color: '#8C98FF' }}>  Security monitor: {failedAttempts} consecutive failed attempts logged.</div>
                  <div className="sim-log-line" style={{ color: '#6B7280' }}>/* Threshold: 5 failed attempts will trigger automated 15-minute freeze. */</div>
                </>
              ) : (
                <>
                  <div className="sim-log-line"><span className="sim-prompt">&gt;</span> POST /v1/auth/verify-login (Attempt 5/5)</div>
                  <div className="sim-log-line" style={{ color: '#FF5A5F', fontWeight: 500 }}>← HTTP/1.1 403 FORBIDDEN (Account Locked)</div>
                  <div className="sim-log-line"><span className="sim-lockout-badge">BRUTE-FORCE LOCKOUT TRIGGERED: 15-MINUTE TIMED FREEZE</span></div>
                  <div className="sim-log-line" style={{ color: '#FF5A5F' }}>
                    &nbsp;&nbsp;&#123; &quot;error&quot;: &quot;Account locked due to too many failed attempts.&quot;, &quot;lockedUntil&quot;: &quot;2026-09-22T17:45:00Z&quot; &#125;
                  </div>
                  <div className="sim-log-line" style={{ color: '#10B981' }}>  IP Rate-Limiting + Tenant Anomaly Alert dispatched to SIEM webhook.</div>
                </>
              )
            )}
          </div>

          <div className="sim-actions">
            {mode === 'mfa' && (
              step === 0 ? (
                <button type="button" className="sim-action-btn" onClick={() => setStep(1)}>
                  <KeyRound size={14} />
                  Provide Authenticator (TOTP) Code
                </button>
              ) : (
                <button type="button" className="sim-action-btn" onClick={() => setStep(0)}>
                  <RotateCcw size={14} />
                  Replay MFA Flow
                </button>
              )
            )}

            {mode === 'passkey' && (
              step === 0 ? (
                <button type="button" className="sim-action-btn" onClick={() => setStep(1)}>
                  <Fingerprint size={14} />
                  Simulate TouchID / Biometric Tap
                </button>
              ) : (
                <button type="button" className="sim-action-btn" onClick={() => setStep(0)}>
                  <RotateCcw size={14} />
                  Re-test Biometrics
                </button>
              )
            )}

            {mode === 'lockout' && (
              failedAttempts < 5 ? (
                <button
                  type="button"
                  className="sim-action-btn"
                  onClick={() => setFailedAttempts(prev => prev + 1)}
                  style={{ background: 'rgba(255, 90, 95, 0.15)', color: '#FF5A5F', border: '1px solid rgba(255, 90, 95, 0.3)' }}
                >
                  <AlertTriangle size={14} />
                  Send Bad MFA Code ({failedAttempts}/5)
                </button>
              ) : (
                <button
                  type="button"
                  className="sim-action-btn"
                  onClick={() => { setFailedAttempts(1); setStep(0); }}
                >
                  <RotateCcw size={14} />
                  Reset Defense Sandbox
                </button>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthSimulator;
